/* eslint-disable @typescript-eslint/no-explicit-any */
import { get13FChartExternalTooltip } from "./get-13f-chart-external-tooltip";
import { Translation } from "@/data/translation";
import { ChartComponentParams, ChartKey } from "@/types/chart";
import { formatMap } from "@/utils/format-number";
import { ChartOptions } from "chart.js";

/**
 * Get chart options based on chart key, this is used to override the default options
 */

export const getChartKeyOptions = ({
  chartKey,
  valueFormatType,
  t,
}: {
  chartKey: ChartKey;
  valueFormatType?: ChartComponentParams["valueFormatType"];
  t: Translation;
}): ChartOptions<"bar" | "line"> => {
  switch (chartKey) {
    case "roa":
    case "ros":
    case "roe":
    case "roic":
    case "custom_portfolio_returns":
      return {};
    case "eps_yoy":
    case "revenue_yoy":
    case "eps":
    case "revenue":
    case "13f_symbol_capital_flow":
      return {
        scales: {
          y: {
            ticks: {
              color: "#0584C7",
            },
          },
          x: {
            grid: {
              display: false,
            },
          },
        },
      };
    case "eps_forecast":
    case "revenue_forecast":
      return {
        scales: {
          y: {
            beginAtZero: false,
          },
        },
        plugins: {
          tooltip: {
            callbacks: {
              label: (context: any) => {
                const label = context.dataset.label;
                const value = context.parsed.y;
                // hide forecast labels for last point of past data
                const targetIndex =
                  context.dataset?.data.reduce((acc: number, curr: any) => {
                    if (curr) return acc;
                    return acc + 1;
                  }, 0) ?? -1;
                if (context.dataIndex === targetIndex) {
                  if (context.datasetIndex > 0) return "";
                }
                return valueFormatType
                  ? `${label}: ${formatMap[valueFormatType](value)}`
                  : `${label}: ${value}`;
              },
            },
          },
        },
      };
    case "custom_portfolio_weights":
      return {
        scales: {
          x: {
            stacked: true,
            grid: {
              display: false,
            },
          },
          y: {
            max: 1,
            stacked: true,
          },
        },
        plugins: {
          legend: {
            labels: {
              usePointStyle: true,
            },
          },
        },
      };
    case "13f_investor_holding_weights":
    case "13f_investor_industrial_distribution":
      return {
        scales: {
          x: {
            stacked: true,
            grid: {
              display: false,
            },
          },
          y: {
            max: 1,
            stacked: true,
          },
        },
        plugins: {
          legend: {
            labels: {
              usePointStyle: true,
            },
          },
          tooltip: {
            enabled: false,
            position: "nearest",
            external: get13FChartExternalTooltip,
            itemSort(a: any, b: any) {
              // put others to the end
              if (a.dataset.label === t.chip.others) return 1;
              if (b.dataset.label === t.chip.others) return -1;
              return (
                (b.dataset.data[a.dataIndex] ?? 0) -
                (a.dataset.data[b.dataIndex] ?? 0)
              );
            },
          },
        },
      };
    case "13f_investor_holding_cost":
      return {
        scales: {
          y: {
            position: "left",
          },
          x: {
            grid: {
              display: false,
            },
          },
          y1: {
            display: false,
          },
        },
      };
    case "financial_metrices_basic_chart":
      return {
        maintainAspectRatio: false,
        plugins: {
          tooltip: {
            caretPadding: 15,
          },
        },
        elements: {
          point: {
            radius: 0,
            hoverRadius: 3,
          },
        },
      };
    default:
      return {};
  }
};
